import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Stack, Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useChargingSessionsV1 } from 'src/Libs/e-mobility/charging-sessions'
import ChargingSessionsOverviewDialog from '../../ChargingSessionsOverviewDialog'
import ChargingSessionsOverviewSkeleton from '../../ChargingSessionsOverviewSkeleton'
import ChargingSessionChartContainer from '../../emobility-charging-sessions/ChargingSessionChartContainer'
import ChargingSessionTable from '../../emobility-charging-sessions/ChargingSessionTable'
import { ChargeDurationCard } from '../../emobility-charging-sessions/stat-cards/ChargeDurationCard'
import { SolarEnergyCard } from '../../emobility-charging-sessions/stat-cards/SolarEnergyCard'
import { TotalEnergyCard } from '../../emobility-charging-sessions/stat-cards/TotalEnergyCard'
import { UNIT_FACTOR_WATT_TO_WATTHOUR } from 'src/Configuration/defaults'
import { WallboxHardwareModel } from 'src/Libs/wallboxes/models'

export interface EMobilityWallboxChargingSessionsOverviewProps {
  open: boolean
  onClose: () => void
  place: number
  wallboxId: string
  storageId: string
  wallboxName: string
  wallboxes: WallboxHardwareModel[]
}

const useStyles = makeStyles()(theme => ({
  cardStats: {
    marginTop: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
}))

const EMobilityWallboxChargingSessionsOverview: React.FC<EMobilityWallboxChargingSessionsOverviewProps> = ({
  open,
  onClose,
  place,
  wallboxId,
  storageId,
  wallboxName,
  wallboxes,
}): JSX.Element => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { chargingSessions, isLoading: isChargingSessionsLoading } = useChargingSessionsV1(storageId)
  const sessions = chargingSessions ? chargingSessions.filter(item => item.wallbox.id === wallboxId) : []
  const totalChargeTime = sessions.reduce((a, b) => a + b.chargeTimeActive, 0)
  const totalEnergyKWh = sessions.reduce((a, b) => a + b.energyAll, 0) / UNIT_FACTOR_WATT_TO_WATTHOUR
  const totalSolarEnergyKWh = sessions.reduce((a, b) => a + b.energySolar, 0) / UNIT_FACTOR_WATT_TO_WATTHOUR

  return (
    <ChargingSessionsOverviewDialog
      open={open}
      place={place}
      title={t('emobility.wallbox.wallboxDialogTitle', { wallbox: wallboxName, id: wallboxId })}
      onClose={onClose}
    >
      {isChargingSessionsLoading && <ChargingSessionsOverviewSkeleton />}
      {!isChargingSessionsLoading && (
        <Box>
          <Stack direction="row" rowGap={2} columnGap={2} className={classes.cardStats} flexWrap="wrap">
            <ChargeDurationCard chargeDurationMilliseconds={totalChargeTime} />
            <TotalEnergyCard energyKWh={totalEnergyKWh} />
            <SolarEnergyCard energyKWh={totalSolarEnergyKWh} />
          </Stack>

          <Box className={classes.tableContainer}>
            <ChargingSessionTable chargingSessions={sessions} wallboxes={wallboxes} />
          </Box>

          <ChargingSessionChartContainer chargingSessions={sessions} />
        </Box>
      )}
    </ChargingSessionsOverviewDialog>
  )
}

export default EMobilityWallboxChargingSessionsOverview
